/**
 * AzureStackTable (Plan v9 §4.2 prototype rework).
 *
 * Renders the Azure stack footer table. Mirrors the prototype's
 * embedding model + search index + events indexed + provenance writes
 * + retraining status rows. Stack identifiers (e.g. `ada-002`,
 * `scrips-cc-index`) render in monospace via the `mono` flag.
 *
 * Data source: `MetricsSnapshot.azureStackRows`. Demo Mode supplies
 * the canned baseline; Live Mode populates from the runtime config +
 * App Insights queries.
 *
 * HC2 token-only: every color, spacing, radius, font-family value
 * resolves through Signal DS tokens via Tailwind. The mono font
 * family is the Tailwind `font-mono` utility which the tailwind
 * config wires to `ui-monospace, SFMono-Regular, Menlo, monospace`
 * (see `tailwind.config.ts`).
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: semantic `<table>` with caption + scoped row headers so
 *   SR users can navigate by row label.
 */
import type { AzureStackRow } from '../../services/metrics/types';

export interface AzureStackTableProps {
  /** Footer rows. */
  rows: ReadonlyArray<AzureStackRow>;
  /** Optional className for the outer wrapper. */
  className?: string;
}

export function AzureStackTable({ rows, className }: AzureStackTableProps) {
  const composed = [
    'flex flex-col gap-gutter-md rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section
      aria-labelledby="azure-stack-table-title"
      data-testid="azure-stack-table"
      className={composed}
    >
      <header className="flex flex-col gap-gutter-sm">
        <h3
          id="azure-stack-table-title"
          className="text-signal-lg font-semibold text-text-primary"
        >
          Azure Stack
        </h3>
        <p className="text-signal-sm text-text-secondary">
          The infrastructure that makes the loop work. No model
          retraining required.
        </p>
      </header>
      <table
        data-testid="azure-stack-table-grid"
        className="w-full text-left text-signal-sm"
      >
        <caption className="sr-only">
          Azure stack: embedding model, search index, events indexed,
          provenance writes, model retraining status.
        </caption>
        <thead>
          <tr className="border-b border-surface-border text-text-tertiary">
            <th
              scope="col"
              className="py-gutter-sm pr-gutter-md text-signal-xs uppercase tracking-wide"
            >
              Component
            </th>
            <th
              scope="col"
              className="py-gutter-sm text-signal-xs uppercase tracking-wide"
            >
              Value
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.label}
              data-testid={`azure-stack-row-${row.label.replace(/\s+/g, '-').toLowerCase()}`}
              className="border-b border-surface-border last:border-b-0"
            >
              <th
                scope="row"
                className="py-gutter-sm pr-gutter-md font-medium text-text-primary"
              >
                {row.label}
              </th>
              <td
                className={
                  row.mono
                    ? 'py-gutter-sm font-mono text-signal-sm text-text-primary'
                    : 'py-gutter-sm text-signal-sm text-text-primary'
                }
                data-testid={`azure-stack-value-${row.label.replace(/\s+/g, '-').toLowerCase()}`}
                data-mono={row.mono ? 'true' : 'false'}
              >
                {row.value}
              </td>
            </tr>
          ))}
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={2}
                data-testid="azure-stack-table-empty"
                className="py-gutter-md text-signal-sm text-text-tertiary"
              >
                No stack data available.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </section>
  );
}
